import React, { useEffect, useState, Fragment } from 'react';

const API_BASE = (import.meta.env.VITE_API_BASE as string) || 'https://meinsolarstart.ch/__admin';

interface RecentClickRow {
  int_clickid: string;
  vid: string;
  first_seen: string;
  last_seen: string;
  event_count: number;
  host: string | null;
  quiz_id: string | null;
  quiz_slug: string | null;
  country: string | null;
  city: string | null;
  path: string | null;
  referer: string | null;
  last_event_type: string | null;
  is_mobile: number | null;
  viewport_w: number | null;
  viewport_h: number | null;
  voluum_clickid: string | null;
  reached_quiz_start: number;
  reached_quiz_complete: number;
  reached_form_view: number;
  reached_form_submit_attempt: number;
  reached_lead_created: number;
  reached_lead_submit_failed: number;
  reached_quiz_abandon: number;
  is_inactive: number;        // 1 if started quiz, no terminal event, no activity in 15+ min
  max_step_index: number | null;
  lcp_ms: number | null;
  ttfb_ms: number | null;
  cls: number | null;
  inp_ms: number | null;
  tab_switches: number | null;
  idle_time_ms: number | null;
  back_clicks_total: number | null;
  active_time_ms: number | null;
  time_in_quiz_ms_abandon: number | null;
  total_steps: number | null;
}

interface DashboardData {
  range: string;
  totals: { events: number; visitors: number; clicks: number };
  recent_clicks: RecentClickRow[];
}

interface DimensionsData {
  dimensions: string[];
  hosts?: string[];
}

interface GroupByData {
  dim: string;
  range: string;
  rows: Array<{ key: string | null; events: number; visitors: number; clicks: number }>;
}

interface ClickEvent {
  id: number;
  ts: string;
  vid: string;
  int_clickid: string;
  host: string | null;
  kind: string;
  event_type: string | null;
  path: string | null;
  query_json: string | null;
  referer: string | null;
  ip: string | null;
  ua: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  voluum_clickid: string | null;
  experiment: string | null;
  variant: string | null;
  quiz_id: string | null;
  quiz_slug: string | null;
  metadata: string | null;
}

interface ClickDetailData {
  int_clickid: string;
  events: ClickEvent[];
}

interface PathsData {
  range: string;
  paths: Array<{ path: string; count: number }>;
}

interface FunnelData {
  range: string;
  path: string;
  quiz_slug: string | null;
  stages: Array<{
    key: string;
    label: string;
    kind: 'macro' | 'step';
    step_index?: number;
    node_id?: string;
    count: number;
  }>;
}

interface FunnelBucket {
  variant_id: string;
  segment: string;
  visitors: number;
  stages: FunnelData['stages'];
}

interface ExperimentBucket {
  variant_id: string;
  visitors: number;
  leads: number;
}

interface ExperimentPathRollup {
  path: string;
  buckets: ExperimentBucket[];
}

interface ExperimentRollup {
  code: string;
  paths: ExperimentPathRollup[];
}

interface ExperimentsData {
  range: string;
  experiments: ExperimentRollup[];
}

interface FunnelCompareData {
  range: string;
  path: string;
  quiz_slug: string | null;
  compare_code: string;
  buckets: FunnelBucket[];
}

interface MetricSummary {
  name: 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB' | string;
  count: number;
  p50: number | null;
  p75: number | null;
  p95: number | null;
  rating_counts: { good?: number; 'needs-improvement'?: number; poor?: number };
}

interface WebVitalsData {
  range: string;
  path: string | null;
  all: MetricSummary[];
  mobile: MetricSummary[];
  desktop: MetricSummary[];
}

interface AbandonData {
  range: string;
  path: string;
  abandons: Array<{
    last_node_id: string | null;
    last_step_index: number | null;
    abandon_count: number;
    avg_time_in_quiz_ms: number | null;
  }>;
}

interface FormHealthData {
  range: string;
  path: string;
  fields: Array<{
    field_label: string;
    field_type: string | null;
    focus_count: number;
    blur_count: number;
    filled_count: number;
    fill_rate: number | null;
    avg_duration_ms: number | null;
    p50_duration_ms: number | null;
    p95_duration_ms: number | null;
    error_count: number;
    error_rate: number | null;
  }>;
}

function useToken() {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem('admin_token'));
  const setToken = (t: string | null) => {
    if (t) localStorage.setItem('admin_token', t);
    else localStorage.removeItem('admin_token');
    setTokenState(t);
  };
  return [token, setToken] as const;
}

function useTheme() {
  const [theme, setThemeState] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);
  const toggle = () => setThemeState((t) => t === 'dark' ? 'light' : 'dark');
  return [theme, toggle] as const;
}

function Login({ onLogin }: { onLogin: (t: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>flow-tracker-lite</h1>
        <p className="muted">Enter the admin token to view the dashboard.</p>
        <input
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Bearer token"
          onKeyDown={(e) => { if (e.key === 'Enter' && val) onLogin(val); }}
        />
        <button onClick={() => onLogin(val)} disabled={!val}>Sign in</button>
      </div>
    </div>
  );
}

function Card({ label, value }: { label: string; value: number }) {
  return (
    <div className="card">
      <div className="card-label">{label}</div>
      <div className="card-value">{value.toLocaleString()}</div>
    </div>
  );
}

function GroupByPanel({
  dimensions,
  dim,
  setDim,
  data,
}: {
  dimensions: string[];
  dim: string;
  setDim: (d: string) => void;
  data: GroupByData | null;
}) {
  return (
    <section className="groupby">
      <h2>Group by</h2>
      <div className="groupby-controls">
        <label htmlFor="dim-select">Dimension:</label>
        <select id="dim-select" value={dim} onChange={(e) => setDim(e.target.value)}>
          {dimensions.length === 0 ? (
            <option value={dim}>{dim}</option>
          ) : dimensions.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
      <table>
        <thead>
          <tr>
            <th>{dim}</th>
            <th className="num">Events</th>
            <th className="num">Visitors</th>
            <th className="num">Clicks</th>
          </tr>
        </thead>
        <tbody>
          {!data || data.rows.length === 0 ? (
            <tr><td className="muted" colSpan={4}>No data</td></tr>
          ) : data.rows.map((r, i) => (
            <tr key={`${r.key ?? '__empty__'}-${i}`}>
              <td>{r.key === null || r.key === '' ? '(empty)' : r.key}</td>
              <td className="num">{r.events.toLocaleString()}</td>
              <td className="num">{r.visitors.toLocaleString()}</td>
              <td className="num">{r.clicks.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function flag(code: string | null): string {
  if (!code) return '';
  const c = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return c;
  const A = 0x1f1e6;
  const codepoints = [c.charCodeAt(0) - 65 + A, c.charCodeAt(1) - 65 + A];
  try {
    return String.fromCodePoint(...codepoints);
  } catch {
    return c;
  }
}

type VitalRating = 'good' | 'needs-improvement' | 'poor';

function rateVital(name: 'LCP' | 'TTFB' | 'INP' | 'CLS' | 'FCP', value: number): VitalRating {
  switch (name) {
    case 'LCP': return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'TTFB': return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    case 'INP': return value <= 200 ? 'good' : value <= 500 ? 'needs-improvement' : 'poor';
    case 'CLS': return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FCP': return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
  }
}

function ratingGlyph(r: VitalRating): string {
  return r === 'good' ? '✓' : r === 'needs-improvement' ? '⚠' : '✗';
}

function vitalDotClass(
  metric: 'LCP' | 'TTFB' | 'INP' | 'CLS',
  value: number | null,
): 'good' | 'needs-improvement' | 'poor' | 'none' {
  if (value === null || value === undefined || Number.isNaN(value)) return 'none';
  return rateVital(metric, value);
}

function vitalDotTitle(
  metric: 'LCP' | 'TTFB' | 'INP' | 'CLS',
  value: number | null,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return `${metric} —`;
  }
  const rating = rateVital(metric, value);
  const display = metric === 'CLS' ? value.toFixed(2) : `${Math.round(value).toLocaleString()}ms`;
  return `${metric} ${display} (${rating})`;
}

function formatTs(ts: string): string {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}:${ss}`;
}

function refererHost(raw: string | null): string {
  if (!raw) return 'direct';
  try {
    const u = new URL(raw);
    return u.hostname || raw;
  } catch {
    return raw;
  }
}

function deriveStatus(r: RecentClickRow): { label: string; cls: string } {
  // Check failed first: the frontend fires lead_created optimistically AND
  // lead_submit_failed when no leadId comes back, so both flags can be set.
  if (r.reached_lead_submit_failed === 1) return { label: 'LEAD FAILED', cls: 'lead-failed' };
  if (r.reached_lead_created === 1) return { label: 'LEAD ✓', cls: 'lead' };
  if (r.reached_form_submit_attempt === 1) return { label: 'SUBMITTED', cls: 'submitted' };
  if (r.reached_quiz_abandon === 1) return { label: 'ABANDONED', cls: 'abandoned' };
  if (r.is_inactive === 1) return { label: 'ABANDONED (idle)', cls: 'abandoned' };
  if (r.reached_form_view === 1) return { label: 'FORM', cls: 'form' };
  if (r.reached_quiz_complete === 1) return { label: 'COMPLETED', cls: 'completed' };
  if (r.reached_quiz_start === 1) return { label: 'IN QUIZ', cls: 'in-quiz' };
  return { label: 'VISITED', cls: 'visited' };
}

function timeInQuizMs(r: RecentClickRow): number | null {
  if (r.reached_quiz_complete === 1 && r.active_time_ms !== null) return r.active_time_ms;
  if (r.reached_quiz_abandon === 1 && r.time_in_quiz_ms_abandon !== null) return r.time_in_quiz_ms_abandon;
  const a = new Date(r.first_seen).getTime();
  const b = new Date(r.last_seen).getTime();
  if (Number.isFinite(a) && Number.isFinite(b) && b >= a) return b - a;
  return null;
}

type ClickFilters = {
  country: string;          // '' = all
  status: string;           // '' = all (matches deriveStatus().key)
  device: '' | 'mobile' | 'desktop';
  path: string;             // '' = all
};

function distinctValues<T>(rows: T[], pick: (r: T) => string | null | undefined): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = pick(r);
    if (v) set.add(v);
  }
  return Array.from(set).sort();
}

function RecentClicksTable({
  rows,
  onSelect,
  selectedClickid,
  token,
  onUnauthorized,
}: {
  rows: RecentClickRow[];
  onSelect: (id: string | null) => void;
  selectedClickid: string | null;
  token: string;
  onUnauthorized: () => void;
}) {
  const [filters, setFilters] = useState<ClickFilters>({ country: '', status: '', device: '', path: '' });

  const countries = distinctValues(rows, (r) => r.country);
  const paths = distinctValues(rows, (r) => r.path);
  const statuses = distinctValues(rows, (r) => deriveStatus(r).cls);

  const filtered = rows.filter((r) => {
    if (filters.country && r.country !== filters.country) return false;
    if (filters.path && r.path !== filters.path) return false;
    if (filters.status && deriveStatus(r).cls !== filters.status) return false;
    if (filters.device === 'mobile' && r.is_mobile !== 1) return false;
    if (filters.device === 'desktop' && r.is_mobile !== 0) return false;
    return true;
  });

  const anyFilter = filters.country || filters.path || filters.status || filters.device;

  const filterControls = (
    <div className="recent-filters">
      <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })}>
        <option value="">All countries</option>
        {countries.map((c) => <option key={c} value={c}>{flag(c)} {c}</option>)}
      </select>
      <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
        <option value="">All status</option>
        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.device} onChange={(e) => setFilters({ ...filters, device: e.target.value as ClickFilters['device'] })}>
        <option value="">All devices</option>
        <option value="mobile">📱 Mobile</option>
        <option value="desktop">🖥️ Desktop</option>
      </select>
      <select value={filters.path} onChange={(e) => setFilters({ ...filters, path: e.target.value })}>
        <option value="">All paths</option>
        {paths.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      {anyFilter && (
        <button className="ghost" onClick={() => setFilters({ country: '', status: '', device: '', path: '' })}>
          Clear
        </button>
      )}
      <span className="muted small recent-filter-count">
        {filtered.length === rows.length
          ? `${rows.length} clicks`
          : `${filtered.length} of ${rows.length} clicks`}
      </span>
    </div>
  );

  if (rows.length === 0) return <><>{filterControls}</><p className="muted">No clicks.</p></>;
  if (filtered.length === 0) return (
    <>
      {filterControls}
      <p className="muted">No clicks match these filters.</p>
    </>
  );

  return (
    <>
    {filterControls}
    <table className="recent-table">
      <thead>
        <tr>
          <th>Time</th>
          <th>Status</th>
          <th>Path</th>
          <th>Geo</th>
          <th>Dev</th>
          <th>Step</th>
          <th>Time in quiz</th>
          <th>Vitals</th>
          <th className="num">Back</th>
          <th className="num">Tabs</th>
          <th className="num">Events</th>
          <th>int_clickid</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((r) => {
          const isSelected = selectedClickid === r.int_clickid;
          const status = deriveStatus(r);
          const tQuizMs = timeInQuizMs(r);
          const firstSeenDate = new Date(r.first_seen);
          const timeStr = firstSeenDate.toLocaleTimeString();
          const fullDate = firstSeenDate.toLocaleString();
          const idShort = r.int_clickid.slice(0, 8);
          const flagEmoji = flag(r.country);
          const geoTitle = r.city
            ? `${r.country ?? ''}${r.country && r.city ? ' · ' : ''}${r.city}`
            : (r.country ?? '');
          const geoCell = r.country
            ? `${flagEmoji ? flagEmoji + ' ' : ''}${r.country}`
            : '';

          let devCell = '';
          let devTitle = '';
          if (r.is_mobile === 1) {
            devCell = '📱';
            devTitle = r.viewport_w && r.viewport_h ? `mobile · ${r.viewport_w}×${r.viewport_h}` : 'mobile';
          } else if (r.is_mobile === 0) {
            devCell = '🖥️';
            devTitle = r.viewport_w && r.viewport_h ? `desktop · ${r.viewport_w}×${r.viewport_h}` : 'desktop';
          }

          let stepCell = '—';
          if (r.max_step_index !== null) {
            const n = r.max_step_index + 1;
            stepCell = r.total_steps ? `${n}/${r.total_steps}` : `${n}`;
          }

          const quizCell = tQuizMs !== null ? formatMmSs(tQuizMs) : '—';
          const backCell = r.back_clicks_total !== null && r.back_clicks_total > 0
            ? r.back_clicks_total.toLocaleString()
            : '';
          const tabsCell = r.tab_switches !== null && r.tab_switches > 0
            ? r.tab_switches.toLocaleString()
            : '';

          return (
            <Fragment key={r.int_clickid}>
            <tr
              className={`clickable${isSelected ? ' selected' : ''}`}
              onClick={() => onSelect(isSelected ? null : r.int_clickid)}
            >
              <td title={fullDate}>{timeStr}</td>
              <td>
                <span className={`status-badge ${status.cls}`}>{status.label}</span>
              </td>
              <td className="path" title={r.path ?? ''}>{r.path ?? ''}</td>
              <td title={geoTitle}>{geoCell}</td>
              <td title={devTitle}>{devCell}</td>
              <td>{stepCell}</td>
              <td>{quizCell}</td>
              <td>
                <span className="vital-dots">
                  <span
                    className={`vital-dot ${vitalDotClass('LCP', r.lcp_ms)}`}
                    title={vitalDotTitle('LCP', r.lcp_ms)}
                  />
                  <span
                    className={`vital-dot ${vitalDotClass('TTFB', r.ttfb_ms)}`}
                    title={vitalDotTitle('TTFB', r.ttfb_ms)}
                  />
                  <span
                    className={`vital-dot ${vitalDotClass('CLS', r.cls)}`}
                    title={vitalDotTitle('CLS', r.cls)}
                  />
                  <span
                    className={`vital-dot ${vitalDotClass('INP', r.inp_ms)}`}
                    title={vitalDotTitle('INP', r.inp_ms)}
                  />
                </span>
              </td>
              <td className="num">{backCell}</td>
              <td className="num">{tabsCell}</td>
              <td className="num">{r.event_count.toLocaleString()}</td>
              <td className="idmono" title={r.int_clickid}>{idShort}</td>
            </tr>
            {isSelected && (
              <tr className="detail-row">
                <td colSpan={12}>
                  <ClickDrilldown
                    clickid={r.int_clickid}
                    token={token}
                    onClose={() => onSelect(null)}
                    onUnauthorized={onUnauthorized}
                  />
                </td>
              </tr>
            )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
    </>
  );
}

function formatMetadata(raw: string | null): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

function ClickDrilldown({
  clickid,
  token,
  onClose,
  onUnauthorized,
}: {
  clickid: string;
  token: string;
  onClose: () => void;
  onUnauthorized: () => void;
}) {
  const [detail, setDetail] = useState<ClickDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/click/${encodeURIComponent(clickid)}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (res.status === 401) { onUnauthorized(); return; }
        if (!res.ok) throw new Error(`click: ${res.status} ${await res.text()}`);
        const j = (await res.json()) as ClickDetailData;
        if (!cancelled) setDetail(j);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickid, token]);

  const latest = detail && detail.events.length > 0 ? detail.events[detail.events.length - 1] : null;

  return (
    <section className="drilldown">
      <div className="drilldown-header">
        <div>
          <h2>Click drill-down</h2>
          <span className="muted small idmono">{clickid}</span>
        </div>
        <button onClick={onClose} className="ghost">Close</button>
      </div>

      {loading && <p className="muted">Loading…</p>}
      {error && <div className="error">{error}</div>}

      {detail && !loading && !error && (
        <>
          {latest && (
            <div className="drilldown-details">
              <h3>Details</h3>
              <dl>
                <dt>vid</dt><dd className="idmono">{latest.vid}</dd>
                <dt>ip</dt><dd className="idmono">{latest.ip ?? '—'}</dd>
                <dt>geo</dt><dd>{[latest.city, latest.region, latest.country].filter(Boolean).join(' · ') || '—'}</dd>
                <dt>voluum_clickid</dt><dd className="idmono">{latest.voluum_clickid ?? '—'}</dd>
                <dt>experiment</dt><dd>{latest.experiment ?? '—'}</dd>
                <dt>variant</dt><dd>{latest.variant ?? '—'}</dd>
                <dt>referer</dt><dd className="ellipsis-wrap">{latest.referer ?? '—'}</dd>
                <dt>ua</dt><dd className="ellipsis-wrap">{latest.ua ?? '—'}</dd>
              </dl>
            </div>
          )}

          <ClickWebVitals events={detail.events} />

          {detail.events.length === 0 ? (
            <p className="muted">No events for this click.</p>
          ) : (
            <table className="timeline">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Kind</th>
                  <th>Event</th>
                  <th>Path</th>
                  <th>Country</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {detail.events.map((e) => (
                  <tr key={e.id}>
                    <td>{new Date(e.ts).toLocaleString()}</td>
                    <td>{e.kind}</td>
                    <td>{e.event_type ?? '—'}</td>
                    <td className="ellipsis" title={e.path ?? ''}>{e.path ?? '—'}</td>
                    <td>{e.country ?? '—'}</td>
                    <td className="meta">
                      {e.metadata ? <pre>{formatMetadata(e.metadata)}</pre> : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function FunnelPanel({
  paths,
  path,
  setPath,
  data,
  compareData,
  loading,
  availableCountries,
  country,
  setCountry,
  device,
  setDevice,
  compareCode,
  setCompareCode,
}: {
  paths: Array<{ path: string; count: number }>;
  path: string | null;
  setPath: (p: string) => void;
  data: FunnelData | null;
  compareData: FunnelCompareData | null;
  loading: boolean;
  availableCountries: string[];
  country: string;
  setCountry: (c: string) => void;
  device: '' | 'mobile' | 'desktop';
  setDevice: (d: '' | 'mobile' | 'desktop') => void;
  compareCode: string;
  setCompareCode: (s: string) => void;
}) {
  const [compareInput, setCompareInput] = useState(compareCode);
  useEffect(() => { setCompareInput(compareCode); }, [compareCode]);
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [simEnabled, setSimEnabled] = useState(false);
  const [boosts, setBoosts] = useState<Record<string, number>>({});
  const [copied, setCopied] = useState(false);

  // Sim mode forces all steps expanded so each gets its own slider
  const effectiveStepsExpanded = stepsExpanded || simEnabled;
  const topCount = data && data.stages.length > 0 ? Math.max(1, data.stages[0].count) : 1;

  // Cascade simulated counts: each stage = previous sim * (base step rate + boost)
  const simCounts: number[] = [];
  if (data && data.stages.length > 0) {
    simCounts.push(data.stages[0].count);
    for (let i = 1; i < data.stages.length; i++) {
      const realPrev = data.stages[i - 1].count;
      const real = data.stages[i].count;
      const baseRate = realPrev > 0 ? real / realPrev : 0;
      const boost = (boosts[data.stages[i].key] ?? 0) / 100;
      const newRate = Math.max(0, Math.min(1, baseRate + boost));
      simCounts.push(Math.round(simCounts[i - 1] * newRate));
    }
  }

  const stageCount = data?.stages.length ?? 0;
  const visits = stageCount > 0 ? data!.stages[0].count : 0;
  const actualLeads = stageCount > 0 ? data!.stages[stageCount - 1].count : 0;
  const simLeads = stageCount > 0 ? simCounts[stageCount - 1] : 0;
  const actualRate = visits > 0 ? (actualLeads / visits) * 100 : 0;
  const simRate = visits > 0 ? (simLeads / visits) * 100 : 0;
  const hasBoosts = Object.values(boosts).some((v) => v > 0);

  // Collect step-row indices for collapse logic
  const stepIndices = new Set<number>();
  if (data) {
    data.stages.forEach((stage, i) => {
      if (stage.kind === 'step') stepIndices.add(i);
    });
  }
  const stepCount = stepIndices.size;

  const setBoost = (key: string, value: number) => {
    setBoosts((prev) => {
      if (value === 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  };

  const buildClipboardText = (): string => {
    if (!data || data.stages.length === 0) return '';
    const lines: string[] = [];
    lines.push(`# Funnel — ${data.path || '(all paths)'}`);
    const meta = [`Range: ${data.range}`];
    if (country) meta.push(`Country: ${country}`); else meta.push('Country: All');
    if (device) meta.push(`Device: ${device}`); else meta.push('Device: All');
    if (data.quiz_slug) meta.push(`Quiz: ${data.quiz_slug}`);
    lines.push(meta.join(' | '));
    lines.push('');
    lines.push(`Visit → Lead conversion: ${actualRate.toFixed(2)}% (${actualLeads.toLocaleString()} of ${visits.toLocaleString()})`);
    lines.push('');
    lines.push('| Stage | Visitors | % of visits | Step rate | Drop-off |');
    lines.push('|---|---:|---:|---:|---:|');
    data.stages.forEach((stage, i) => {
      const prev = i > 0 ? data.stages[i - 1].count : null;
      const pctOfTop = (stage.count / topCount) * 100;
      const stepRate = prev && prev > 0 ? (stage.count / prev) * 100 : null;
      const dropoff = prev && prev > 0 && stage.count < prev ? ((prev - stage.count) / prev) * 100 : null;
      const indent = stage.kind === 'step' ? '  └ ' : '';
      lines.push(
        `| ${indent}${stage.label} | ${stage.count.toLocaleString()} | ${pctOfTop.toFixed(1)}% | ${stepRate !== null ? stepRate.toFixed(1) + '%' : '—'} | ${dropoff !== null ? '−' + dropoff.toFixed(1) + '%' : '—'} |`,
      );
    });

    if (simEnabled && hasBoosts) {
      lines.push('');
      lines.push(`## Simulated (with per-step boosts)`);
      lines.push(`Visit → Lead: ${simRate.toFixed(2)}% (${simLeads.toLocaleString()} leads, +${(simRate - actualRate).toFixed(2)}pp, +${(simLeads - actualLeads).toLocaleString()} vs actual)`);
      lines.push('');
      lines.push('| Stage | Boost | Sim visitors | Sim step rate | Δ visitors |');
      lines.push('|---|---:|---:|---:|---:|');
      data.stages.forEach((stage, i) => {
        const prev = i > 0 ? data.stages[i - 1].count : null;
        const baseRate = prev && prev > 0 ? (stage.count / prev) * 100 : null;
        const boost = boosts[stage.key] ?? 0;
        const newRate = baseRate !== null ? Math.min(100, baseRate + boost) : null;
        const simC = simCounts[i];
        const delta = simC - stage.count;
        const indent = stage.kind === 'step' ? '  └ ' : '';
        lines.push(
          `| ${indent}${stage.label} | ${i === 0 ? '—' : (boost > 0 ? '+' + boost + 'pp' : '0')} | ${simC.toLocaleString()} | ${newRate !== null ? newRate.toFixed(1) + '%' : '—'} | ${i === 0 ? '—' : (delta >= 0 ? '+' : '') + delta.toLocaleString()} |`,
        );
      });
    }

    return lines.join('\n');
  };

  const handleCopy = async () => {
    const text = buildClipboardText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  const renderRow = (
    stage: FunnelData['stages'][number],
    i: number,
    prevRealCount: number | null,
  ): React.ReactNode => {
    const realCount = stage.count;
    const simCount = simCounts[i] ?? realCount;
    const realPctOfTop = (realCount / topCount) * 100;
    const simPctOfTop = (simCount / topCount) * 100;
    const realDropoffPct = prevRealCount && prevRealCount > 0
      ? ((prevRealCount - realCount) / prevRealCount) * 100
      : 0;
    const stepBoost = boosts[stage.key] ?? 0;
    const baseStepRate = prevRealCount && prevRealCount > 0 ? (realCount / prevRealCount) * 100 : 0;
    const newStepRate = Math.min(100, baseStepRate + stepBoost);
    const isFirst = i === 0;

    return (
      <div key={stage.key} className="funnel-row" data-kind={stage.kind}>
        <div className="funnel-label">
          {stage.kind === 'step' && <span className="step-prefix">└</span>}
          {stage.label}
        </div>
        <div className="funnel-bar-wrap">
          <div
            className="funnel-bar"
            style={{ width: `${simEnabled ? simPctOfTop : realPctOfTop}%` }}
          />
          {simEnabled && stepBoost > 0 && (
            <div className="funnel-bar-actual-marker" style={{ left: `${realPctOfTop}%` }} />
          )}
        </div>
        <div className="funnel-count">
          {simEnabled && !isFirst ? (
            <>
              <span className="sim-before">{realCount.toLocaleString()}</span>
              <span className="sim-arrow"> → </span>
              <span className="sim-after">{simCount.toLocaleString()}</span>
            </>
          ) : (
            realCount.toLocaleString()
          )}
        </div>
        <div className="funnel-pct">
          {simEnabled && !isFirst ? (
            <>
              <span className="sim-before">{realPctOfTop.toFixed(0)}%</span>
              <span className="sim-arrow"> → </span>
              <span className="sim-after">{simPctOfTop.toFixed(0)}%</span>
            </>
          ) : (
            `${realPctOfTop.toFixed(0)}%`
          )}
        </div>
        <div className="funnel-dropoff">
          {simEnabled && !isFirst ? (
            <div className="funnel-slider-wrap">
              <input
                type="range"
                min={0}
                max={50}
                step={1}
                value={stepBoost}
                onChange={(e) => setBoost(stage.key, Number(e.target.value))}
                className="funnel-slider"
                aria-label={`Boost ${stage.label} conversion`}
              />
              <span className="funnel-slider-label">
                {stepBoost > 0 ? `+${stepBoost}pp` : `${baseStepRate.toFixed(0)}%`}
              </span>
            </div>
          ) : (
            prevRealCount !== null && prevRealCount > 0 && realCount < prevRealCount
              ? `−${realDropoffPct.toFixed(0)}%`
              : '—'
          )}
        </div>
      </div>
    );
  };

  // Build rendered rows
  const rows: React.ReactNode[] = [];
  if (data && data.stages.length > 0) {
    let summaryInserted = false;
    // Track the last non-step count so quiz_complete dropoff is correct when collapsed
    let prevNonStepCount: number | null = null;

    data.stages.forEach((stage, i) => {
      const isStep = stage.kind === 'step';

      if (isStep) {
        if (!effectiveStepsExpanded) {
          // Collapsed: insert summary row once, before the first step
          if (!summaryInserted && stepCount > 0) {
            summaryInserted = true;
            const maxStepCount = Math.max(...Array.from(stepIndices).map((idx) => data.stages[idx].count));
            const summaryPct = (maxStepCount / topCount) * 100;
            rows.push(
              <div
                key="__steps_summary"
                className="funnel-row"
                data-kind="step"
                style={{ cursor: 'pointer' }}
                onClick={() => setStepsExpanded(true)}
              >
                <div className="funnel-label">
                  <span className="step-prefix">└</span>
                  {` Quiz steps (${stepCount}) ▸`}
                </div>
                <div className="funnel-bar-wrap">
                  <div className="funnel-bar" style={{ width: `${summaryPct}%` }} />
                </div>
                <div className="funnel-count">{maxStepCount.toLocaleString()}</div>
                <div className="funnel-pct">{summaryPct.toFixed(0)}%</div>
                <div className="funnel-dropoff">—</div>
              </div>
            );
          }
          // Skip individual step rows when collapsed
          return;
        }

        // Expanded
        const prevCount = i > 0 ? data.stages[i - 1].count : null;
        rows.push(renderRow(stage, i, prevCount));

        // After the last step row when expanded (and sim mode is off), insert collapse toggle
        const nextIsStep = i + 1 < data.stages.length && data.stages[i + 1].kind === 'step';
        if (!nextIsStep && !simEnabled) {
          rows.push(
            <div
              key="__steps_collapse"
              className="funnel-row"
              data-kind="step"
              style={{ cursor: 'pointer' }}
              onClick={() => setStepsExpanded(false)}
            >
              <div className="funnel-label">
                <span className="step-prefix">└</span>
                {' Hide quiz steps ▾'}
              </div>
              <div className="funnel-bar-wrap" />
              <div className="funnel-count" />
              <div className="funnel-pct" />
              <div className="funnel-dropoff" />
            </div>
          );
        }
        return;
      }

      // Non-step row
      const prevStepsExist = stepCount > 0 && !effectiveStepsExpanded;
      const prevWasStepCluster = prevStepsExist && i > 0 && data.stages[i - 1].kind === 'step';
      const effectivePrevCount = prevWasStepCluster
        ? prevNonStepCount
        : i > 0 ? data.stages[i - 1].count : null;

      rows.push(renderRow(stage, i, effectivePrevCount));

      prevNonStepCount = stage.count;
    });
  }

  return (
    <section className="funnel">
      <div className="funnel-header">
        <h2>Funnel</h2>
        <div className="funnel-controls">
          <label>Path:</label>
          <select
            value={path ?? ''}
            onChange={(e) => setPath(e.target.value)}
            disabled={paths.length === 0}
          >
            {paths.length === 0 ? (
              <option value="">{loading ? 'Loading…' : 'No paths'}</option>
            ) : paths.map((p) => (
              <option key={p.path} value={p.path}>{p.path} ({p.count})</option>
            ))}
          </select>
          <label>Country:</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">All</option>
            {availableCountries.map((c) => (
              <option key={c} value={c}>{flag(c)} {c}</option>
            ))}
          </select>
          <label>Device:</label>
          <select value={device} onChange={(e) => setDevice(e.target.value as '' | 'mobile' | 'desktop')}>
            <option value="">All</option>
            <option value="mobile">📱 Mobile</option>
            <option value="desktop">🖥️ Desktop</option>
          </select>
          <label title="Compare by experiment code (e.g. her, fm, cta). Splits the funnel by variant.">Compare:</label>
          <input
            type="text"
            value={compareInput}
            placeholder="code (e.g. her)"
            onChange={(e) => setCompareInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            onBlur={() => setCompareCode(compareInput.trim().toLowerCase())}
            className="exclude-input"
            style={{ width: 110 }}
          />
          {compareCode && (
            <button type="button" className="funnel-sim-reset" onClick={() => setCompareCode('')}>
              Clear
            </button>
          )}
          {(data?.quiz_slug || compareData?.quiz_slug) && <span className="muted small">Quiz: {data?.quiz_slug ?? compareData?.quiz_slug}</span>}
          <button
            type="button"
            className={`funnel-sim-toggle${simEnabled ? ' active' : ''}`}
            onClick={() => setSimEnabled((v) => !v)}
          >
            {simEnabled ? '✓ Simulate' : 'Simulate'}
          </button>
          {simEnabled && hasBoosts && (
            <button type="button" className="funnel-sim-reset" onClick={() => setBoosts({})}>
              Reset
            </button>
          )}
          <button
            type="button"
            className="funnel-copy"
            onClick={handleCopy}
            disabled={!data || data.stages.length === 0}
            title="Copy funnel results as markdown for pasting into AI chat"
          >
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      </div>

      {compareData ? (
        <FunnelCompareView data={compareData} loading={loading} />
      ) : (
        <>
          {data && data.stages.length > 0 && (
            <div className="funnel-summary">
              <div className="funnel-summary-label">Visit → Lead conversion</div>
              <div className="funnel-summary-numbers">
                <div className="funnel-summary-block">
                  <span className="funnel-summary-rate">{actualRate.toFixed(2)}%</span>
                  <span className="funnel-summary-detail">
                    {actualLeads.toLocaleString()} of {visits.toLocaleString()}
                  </span>
                </div>
                {simEnabled && (
                  <>
                    <span className="funnel-summary-arrow">→</span>
                    <div className="funnel-summary-block sim">
                      <span className="funnel-summary-rate">{simRate.toFixed(2)}%</span>
                      <span className="funnel-summary-detail">
                        {simLeads.toLocaleString()} leads
                        {hasBoosts && (
                          <span className="funnel-summary-delta">
                            {' '}(+{(simRate - actualRate).toFixed(2)}pp,
                            {' +'}{(simLeads - actualLeads).toLocaleString()})
                          </span>
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="funnel-rows" data-sim={simEnabled ? 'true' : 'false'}>
            {!data || data.stages.length === 0 ? (
              <p className="muted">{loading ? 'Loading…' : 'No funnel data.'}</p>
            ) : rows}
          </div>
        </>
      )}
    </section>
  );
}

// "Active experiments" list — discovered from D1 over the chosen range.
// One row per (code, path). Click → seeds Path + Compare so the funnel
// below switches to the per-variant comparison view for that test.
function ExperimentsPanel({
  data,
  paths,
  range,
  onPick,
}: {
  data: ExperimentsData | null;
  paths: Array<{ path: string; count: number }>;
  range: string;
  onPick: (path: string, code: string) => void;
}) {
  const rows: Array<{
    code: string;
    path: string;
    visitors: number;
    leads: number;
    rate: number;
    deltaPp: number | null;
    variantCount: number;
    bestVariantId: string | null;
  }> = [];

  for (const exp of data?.experiments ?? []) {
    for (const p of exp.paths) {
      const totalVisitors = p.buckets.reduce((a, b) => a + b.visitors, 0);
      const totalLeads = p.buckets.reduce((a, b) => a + b.leads, 0);
      const overallRate = totalVisitors > 0 ? (totalLeads / totalVisitors) * 100 : 0;
      const control = p.buckets.find((b) => b.variant_id === '0');
      const controlRate =
        control && control.visitors > 0 ? (control.leads / control.visitors) * 100 : null;
      let bestRate = -Infinity;
      let bestVariantId: string | null = null;
      for (const b of p.buckets) {
        if (b.variant_id === '0') continue;
        const r = b.visitors > 0 ? (b.leads / b.visitors) * 100 : 0;
        if (r > bestRate) { bestRate = r; bestVariantId = b.variant_id; }
      }
      const deltaPp =
        controlRate !== null && bestVariantId !== null
          ? bestRate - controlRate
          : null;
      rows.push({
        code: exp.code,
        path: p.path,
        visitors: totalVisitors,
        leads: totalLeads,
        rate: overallRate,
        deltaPp,
        variantCount: p.buckets.length,
        bestVariantId,
      });
    }
  }

  const pathDisplayLabel = (p: string): string => {
    const m = paths.find((x) => x.path === p);
    return m ? `${p} (${m.count})` : p;
  };

  return (
    <section className="experiments">
      <div className="experiments-header">
        <h2>Active experiments</h2>
        <span className="muted small">range: {range}</span>
      </div>
      {!data ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">
          No experiments with traffic in this range. Once a variant test starts
          stamping the funnel_id, it&rsquo;ll show up here.
        </p>
      ) : (
        <table className="experiments-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Path</th>
              <th className="num">Visitors</th>
              <th className="num">Leads</th>
              <th className="num">Conv%</th>
              <th className="num">Δ best</th>
              <th>Variants</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.code}::${r.path}`}>
                <td><code>{r.code}</code></td>
                <td>{pathDisplayLabel(r.path)}</td>
                <td className="num">{r.visitors.toLocaleString()}</td>
                <td className="num">{r.leads.toLocaleString()}</td>
                <td className="num">{r.rate.toFixed(2)}%</td>
                <td className="num">
                  {r.deltaPp === null ? (
                    '—'
                  ) : (
                    <span className={r.deltaPp >= 0 ? 'pos' : 'neg'}>
                      {r.deltaPp >= 0 ? '+' : ''}
                      {r.deltaPp.toFixed(2)}pp
                      {r.bestVariantId ? <> ({r.bestVariantId})</> : null}
                    </span>
                  )}
                </td>
                <td>{r.variantCount}</td>
                <td>
                  <button
                    className="experiments-compare-btn"
                    onClick={() => onPick(r.path, r.code)}
                  >
                    Compare ↓
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Renders one full funnel per bucket, side-by-side in a horizontal layout.
// Each column is a self-contained funnel using the same row shape as the
// regular FunnelPanel (label / bar / count / pct / drop-off).
function FunnelCompareView({ data, loading }: { data: FunnelCompareData; loading: boolean }) {
  if (!data.buckets.length) {
    return (
      <p className="muted">
        {loading ? 'Loading…' : `No variants found for code "${data.compare_code}". Either no events have stamped that segment yet, or the code doesn't match. Try a different code.`}
      </p>
    );
  }

  const summary = (b: FunnelBucket) => {
    const top = b.stages[0]?.count ?? 0;
    const last = b.stages[b.stages.length - 1];
    const leads = last?.key === 'lead_created' ? last.count : 0;
    return { top, leads, rate: top > 0 ? (leads / top) * 100 : 0 };
  };

  const control = data.buckets.find((b) => b.variant_id === '0') ?? data.buckets[0];
  const controlRate = summary(control).rate;

  return (
    <div className="funnel-compare-multi">
      {data.buckets.map((b) => {
        const s = summary(b);
        const isControl = b.variant_id === '0';
        const overallDelta = isControl ? 0 : s.rate - controlRate;
        const topCount = Math.max(1, s.top);
        return (
          <div
            key={b.segment}
            className="funnel-compare-column"
            data-control={isControl ? 'true' : 'false'}
          >
            <div className="funnel-compare-column-header">
              <div className="funnel-compare-col-segment">
                <code>{b.segment}</code>
                {isControl && <span className="funnel-compare-tag">control</span>}
              </div>
              <div className="funnel-compare-col-rate">{s.rate.toFixed(2)}%</div>
              <div className="funnel-compare-col-detail muted small">
                {s.leads.toLocaleString()} / {s.top.toLocaleString()} visit→lead
              </div>
              {!isControl && s.top > 0 && (
                <div
                  className={`funnel-compare-col-delta ${overallDelta >= 0 ? 'pos' : 'neg'}`}
                >
                  {overallDelta >= 0 ? '+' : ''}
                  {overallDelta.toFixed(2)}pp vs control
                </div>
              )}
            </div>
            <div className="funnel-rows">
              {b.stages.map((stage, i) => {
                const prev = i > 0 ? b.stages[i - 1].count : null;
                const pctOfTop = (stage.count / topCount) * 100;
                const dropoffPct =
                  prev && prev > 0
                    ? ((prev - stage.count) / prev) * 100
                    : 0;
                return (
                  <div
                    key={stage.key}
                    className="funnel-row"
                    data-kind={stage.kind}
                  >
                    <div className="funnel-label">
                      {stage.kind === 'step' && <span className="step-prefix">└</span>}
                      {stage.label}
                    </div>
                    <div className="funnel-bar-wrap">
                      <div
                        className="funnel-bar"
                        style={{ width: `${pctOfTop}%` }}
                      />
                    </div>
                    <div className="funnel-count">{stage.count.toLocaleString()}</div>
                    <div className="funnel-pct">{pctOfTop.toFixed(0)}%</div>
                    <div className="funnel-dropoff">
                      {prev !== null && prev > 0 && stage.count < prev
                        ? `−${dropoffPct.toFixed(0)}%`
                        : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatMetricValue(name: string, value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (name === 'CLS') {
    return value.toFixed(2);
  }
  return `${Math.round(value).toLocaleString()}ms`;
}

function WebVitalsPanel({ data }: { data: WebVitalsData | null }) {
  const [segment, setSegment] = useState<'all' | 'mobile' | 'desktop'>('all');
  const metrics: MetricSummary[] = data ? data[segment] ?? [] : [];

  return (
    <section className="web-vitals">
      <div className="web-vitals-header">
        <h2>Web Vitals</h2>
        <div className="wv-segments">
          {(['all', 'mobile', 'desktop'] as const).map((s) => (
            <button
              key={s}
              className={segment === s ? 'active' : ''}
              onClick={() => setSegment(s)}
            >
              {s === 'all' ? 'All' : s === 'mobile' ? 'Mobile' : 'Desktop'}
            </button>
          ))}
        </div>
      </div>
      {data === null || metrics.length === 0 ? (
        <p className="muted">No web vitals data in this range</p>
      ) : (
        <table className="wv-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="num">Count</th>
              <th className="num">p50</th>
              <th className="num">p75</th>
              <th className="num">p95</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const good = m.rating_counts.good ?? 0;
              const ni = m.rating_counts['needs-improvement'] ?? 0;
              const poor = m.rating_counts.poor ?? 0;
              const total = good + ni + poor;
              const goodPct = total > 0 ? (good / total) * 100 : 0;
              const niPct = total > 0 ? (ni / total) * 100 : 0;
              const poorPct = total > 0 ? (poor / total) * 100 : 0;
              return (
                <tr key={m.name}>
                  <td className="metric">{m.name}</td>
                  <td className="num">{m.count.toLocaleString()}</td>
                  <td className="num">{formatMetricValue(m.name, m.p50)}</td>
                  <td className="num">{formatMetricValue(m.name, m.p75)}</td>
                  <td className="num">{formatMetricValue(m.name, m.p95)}</td>
                  <td>
                    <div
                      className="wv-rating-bar"
                      title={`good ${good} / needs-improvement ${ni} / poor ${poor}`}
                    >
                      {goodPct > 0 && <div className="good" style={{ width: `${goodPct}%` }} />}
                      {niPct > 0 && <div className="needs-improvement" style={{ width: `${niPct}%` }} />}
                      {poorPct > 0 && <div className="poor" style={{ width: `${poorPct}%` }} />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  return `${Math.round(ms).toLocaleString()}ms`;
}

function formatMmSs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

function AbandonPointsPanel({ data }: { data: AbandonData | null }) {
  const rows = data?.abandons ?? [];
  const maxCount = rows.length > 0 ? Math.max(1, ...rows.map((r) => r.abandon_count)) : 1;
  return (
    <section className="abandon">
      <h2>Abandon Points</h2>
      <p className="muted small subhead">Where people bail mid-quiz</p>
      {rows.length === 0 ? (
        <p className="muted">No abandons recorded in this range.</p>
      ) : (
        <table className="abandon-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Node</th>
              <th className="num">Count</th>
              <th>Distribution</th>
              <th className="num">Avg time before bailing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = (r.abandon_count / maxCount) * 100;
              const stepLabel = r.last_step_index !== null ? `#${r.last_step_index}` : '—';
              const nodeLabel = r.last_node_id ?? '—';
              return (
                <tr key={`${r.last_node_id ?? 'null'}-${r.last_step_index ?? 'null'}-${i}`}>
                  <td>{stepLabel}</td>
                  <td title={nodeLabel}>{nodeLabel}</td>
                  <td className="num">{r.abandon_count.toLocaleString()}</td>
                  <td className="abandon-bar-cell">
                    <div className="abandon-bar">
                      <div className="abandon-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="num">{formatMmSs(r.avg_time_in_quiz_ms)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FormHealthPanel({ data }: { data: FormHealthData | null }) {
  const rows = data?.fields ?? [];
  return (
    <section className="form-health">
      <h2>Form Field Health</h2>
      <p className="muted small subhead">Per-field engagement on the lead form</p>
      {rows.length === 0 ? (
        <p className="muted">No form interaction in this range.</p>
      ) : (
        <table className="form-health-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th className="num">Focuses</th>
              <th className="num">Fills</th>
              <th className="num">Avg duration</th>
              <th className="num">p95 duration</th>
              <th className="num">Validation errors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f, i) => {
              const fillPct = formatPct(f.fill_rate);
              const errorPct = formatPct(f.error_rate);
              const lowFill = f.fill_rate !== null && f.fill_rate < 0.5 && f.focus_count > 0;
              const highError = f.error_rate !== null && f.error_rate >= 0.2;
              return (
                <tr key={`${f.field_label}-${i}`}>
                  <td>{f.field_label}</td>
                  <td>{f.field_type ?? '—'}</td>
                  <td className="num">{f.focus_count.toLocaleString()}</td>
                  <td className={`num${lowFill ? ' cell-warn' : ''}`}>
                    {f.filled_count.toLocaleString()} ({fillPct})
                  </td>
                  <td className="num">{formatMs(f.avg_duration_ms)}</td>
                  <td className="num">{formatMs(f.p95_duration_ms)}</td>
                  <td className={`num${highError ? ' cell-danger' : ''}`}>
                    {f.error_count.toLocaleString()} ({errorPct})
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ClickWebVitals({ events }: { events: ClickEvent[] }) {
  const vitals = events.filter((e) => e.event_type === 'web_vital');
  if (vitals.length === 0) return null;
  const items = vitals
    .map((e) => {
      let meta: any = e.metadata;
      if (typeof meta === 'string') {
        try {
          meta = JSON.parse(meta);
        } catch {
          return null;
        }
      }
      if (!meta || typeof meta !== 'object') return null;
      const name = String(meta.metric_name ?? '');
      const value = typeof meta.value === 'number' ? meta.value : Number(meta.value);
      const rating = String(meta.rating ?? '');
      if (!name) return null;
      return { id: e.id, name, value: Number.isFinite(value) ? value : null, rating };
    })
    .filter((x): x is { id: number; name: string; value: number | null; rating: string } => x !== null);

  if (items.length === 0) return null;

  return (
    <div className="click-web-vitals">
      <h3>Web Vitals</h3>
      <div className="click-web-vitals-grid">
        {items.map((it) => {
          const ratingClass = it.rating === 'good' || it.rating === 'needs-improvement' || it.rating === 'poor'
            ? it.rating
            : '';
          return (
            <div key={it.id} className="click-wv-item">
              <strong>{it.name}</strong> {formatMetricValue(it.name, it.value)}
              {it.rating && <span className={`badge ${ratingClass}`}>{it.rating}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type RangeMode = '1h' | '24h' | '7d' | '30d' | '90d' | 'all' | 'custom';

// Formats today (and an offset-of-day) as YYYY-MM-DD in the user's locale,
// matching what <input type="date"> exposes. Used to seed defaults when the
// user first picks "custom" without having dates yet.
function isoDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function Dashboard({ token, onLogout, theme, toggleTheme }: { token: string; onLogout: () => void; theme: 'light' | 'dark'; toggleTheme: () => void }) {
  const [range, setRange] = useState<RangeMode>(
    () => (localStorage.getItem('range') as RangeMode) ?? '24h',
  );
  const [customFrom, setCustomFrom] = useState<string>(
    () => localStorage.getItem('range_from') ?? '',
  );
  const [customTo, setCustomTo] = useState<string>(
    () => localStorage.getItem('range_to') ?? '',
  );
  useEffect(() => { localStorage.setItem('range', range); }, [range]);
  useEffect(() => { localStorage.setItem('range_from', customFrom); }, [customFrom]);
  useEffect(() => { localStorage.setItem('range_to', customTo); }, [customTo]);

  // Time-window query params for every loader. Custom takes precedence iff
  // both ends are filled in. `timeKey` is the useEffect dep that fires a
  // refetch on any time-window change without recomputing the param string.
  const [host, setHost] = useState<string>(
    () => localStorage.getItem('host_filter') ?? '',
  );
  useEffect(() => { localStorage.setItem('host_filter', host); }, [host]);
  const [hosts, setHosts] = useState<string[]>([]);
  const useCustom = range === 'custom' && !!customFrom && !!customTo;
  const timeQuery = useCustom ? `from=${customFrom}&to=${customTo}` : `range=${range}`;
  const timeKey = useCustom ? `custom:${customFrom}:${customTo}` : range;
  const hostQuery = host ? `&host=${encodeURIComponent(host)}` : '';
  const [data, setData] = useState<DashboardData | null>(null);
  const [dimensions, setDimensions] = useState<string[]>([]);
  const [dim, setDim] = useState<string>('path');
  const [groupBy, setGroupBy] = useState<GroupByData | null>(null);
  const [paths, setPaths] = useState<Array<{ path: string; count: number }>>([]);
  const [path, setPath] = useState<string | null>(null);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  // Funnel-only filters: single-country include + device toggle.
  // Applied server-side via /__admin/funnel?country_in=XX&is_mobile=...
  const [funnelCountry, setFunnelCountry] = useState<string>(
    () => localStorage.getItem('funnel_country') ?? '',
  );
  const [funnelDevice, setFunnelDevice] = useState<'' | 'mobile' | 'desktop'>(
    () => (localStorage.getItem('funnel_device') as '' | 'mobile' | 'desktop') ?? '',
  );
  const [compareCode, setCompareCode] = useState<string>(
    () => localStorage.getItem('funnel_compare_code') ?? '',
  );
  const [funnelCompare, setFunnelCompare] = useState<FunnelCompareData | null>(null);
  const [experiments, setExperiments] = useState<ExperimentsData | null>(null);
  useEffect(() => { localStorage.setItem('funnel_country', funnelCountry); }, [funnelCountry]);
  useEffect(() => { localStorage.setItem('funnel_device', funnelDevice); }, [funnelDevice]);
  useEffect(() => { localStorage.setItem('funnel_compare_code', compareCode); }, [compareCode]);
  const [webVitals, setWebVitals] = useState<WebVitalsData | null>(null);
  const [abandons, setAbandons] = useState<AbandonData | null>(null);
  const [formHealth, setFormHealth] = useState<FormHealthData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedClickid, setSelectedClickid] = useState<string | null>(null);

  const authHeaders = { authorization: `Bearer ${token}` };

  const loadDashboard = async () => {
    const res = await fetch(`${API_BASE}/dashboard?${timeQuery}${hostQuery}`, { headers: authHeaders });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`dashboard: ${res.status} ${await res.text()}`);
    return (await res.json()) as DashboardData;
  };

  const loadGroupBy = async (d: string) => {
    const res = await fetch(`${API_BASE}/groupby?dim=${encodeURIComponent(d)}&${timeQuery}&limit=200${hostQuery}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`groupby: ${res.status} ${await res.text()}`);
    return (await res.json()) as GroupByData;
  };

  const loadPaths = async () => {
    const res = await fetch(`${API_BASE}/paths?${timeQuery}${hostQuery}`, { headers: authHeaders });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`paths: ${res.status} ${await res.text()}`);
    return (await res.json()) as PathsData;
  };

  const loadFunnel = async (
    p: string,
    codeOverride?: string,
  ): Promise<FunnelData | FunnelCompareData | null> => {
    const params = new URLSearchParams({ path: p });
    if (useCustom) {
      params.set('from', customFrom);
      params.set('to', customTo);
    } else {
      params.set('range', range);
    }
    if (/^[A-Z]{2}$/.test(funnelCountry)) params.set('country_in', funnelCountry);
    if (funnelDevice === 'mobile') params.set('is_mobile', '1');
    if (funnelDevice === 'desktop') params.set('is_mobile', '0');
    const code = (codeOverride ?? compareCode).trim().toLowerCase();
    if (/^[a-z]{2,4}[a-z0-9]{0,3}$/.test(code)) {
      params.set('compare_code', code);
    }
    if (host) params.set('host', host);
    const res = await fetch(`${API_BASE}/funnel?${params.toString()}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`funnel: ${res.status} ${await res.text()}`);
    return (await res.json()) as FunnelData | FunnelCompareData;
  };

  const loadWebVitals = async (p: string) => {
    const res = await fetch(`${API_BASE}/web-vitals?${timeQuery}&path=${encodeURIComponent(p)}${hostQuery}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`web-vitals: ${res.status} ${await res.text()}`);
    return (await res.json()) as WebVitalsData;
  };

  const loadAbandons = async (p: string) => {
    const res = await fetch(`${API_BASE}/abandon-points?${timeQuery}&path=${encodeURIComponent(p)}${hostQuery}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`abandon-points: ${res.status} ${await res.text()}`);
    return (await res.json()) as AbandonData;
  };

  const loadExperiments = async () => {
    const res = await fetch(`${API_BASE}/experiments?${timeQuery}${hostQuery}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`experiments: ${res.status} ${await res.text()}`);
    return (await res.json()) as ExperimentsData;
  };

  const loadFormHealth = async (p: string) => {
    const res = await fetch(`${API_BASE}/form-health?${timeQuery}&path=${encodeURIComponent(p)}${hostQuery}`, {
      headers: authHeaders,
    });
    if (res.status === 401) { onLogout(); return null; }
    if (!res.ok) throw new Error(`form-health: ${res.status} ${await res.text()}`);
    return (await res.json()) as FormHealthData;
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const tasks: Array<Promise<any>> = [loadDashboard(), loadGroupBy(dim), loadExperiments()];
      const funnelIdx = path ? tasks.push(loadFunnel(path) as Promise<FunnelData | FunnelCompareData | null>) - 1 : -1;
      const wvIdx = path ? tasks.push(loadWebVitals(path)) - 1 : -1;
      const abandonIdx = path ? tasks.push(loadAbandons(path)) - 1 : -1;
      const formHealthIdx = path ? tasks.push(loadFormHealth(path)) - 1 : -1;
      const results = await Promise.all(tasks);
      const dash = results[0] as DashboardData | null;
      const gb = results[1] as GroupByData | null;
      const exp = results[2] as ExperimentsData | null;
      if (dash) setData(dash);
      if (gb) setGroupBy(gb);
      if (exp) setExperiments(exp);
      if (funnelIdx >= 0 && results[funnelIdx]) {
        const fr = results[funnelIdx] as FunnelData | FunnelCompareData;
        if ('buckets' in fr) { setFunnelCompare(fr); setFunnel(null); }
        else { setFunnel(fr); setFunnelCompare(null); }
      }
      if (wvIdx >= 0 && results[wvIdx]) setWebVitals(results[wvIdx] as WebVitalsData);
      if (abandonIdx >= 0 && results[abandonIdx]) setAbandons(results[abandonIdx] as AbandonData);
      if (formHealthIdx >= 0 && results[formHealthIdx]) setFormHealth(results[formHealthIdx] as FormHealthData);
      if (!path) {
        setWebVitals(null);
        setAbandons(null);
        setFormHealth(null);
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  // Fetch dimensions once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/dimensions`, { headers: authHeaders });
        if (res.status === 401) { onLogout(); return; }
        if (!res.ok) throw new Error(`dimensions: ${res.status} ${await res.text()}`);
        const j = (await res.json()) as DimensionsData;
        if (!cancelled) {
          setDimensions(j.dimensions ?? []);
          setHosts(j.hosts ?? []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch paths on mount and on range/token change. Default path to first entry.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const j = await loadPaths();
        if (cancelled || !j) return;
        setPaths(j.paths ?? []);
        if ((j.paths ?? []).length > 0) {
          setPath((curr) => {
            if (curr && j.paths.some((p) => p.path === curr)) return curr;
            return j.paths[0].path;
          });
        } else {
          setPath(null);
          setFunnel(null);
          setFunnelCompare(null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeKey, token, host]);

  // Fetch funnel when path / range / filters / compareCode change.
  useEffect(() => {
    if (!path) { setFunnel(null); setFunnelCompare(null); return; }
    let cancelled = false;
    setFunnelLoading(true);
    (async () => {
      try {
        const j = await loadFunnel(path);
        if (cancelled || !j) return;
        if ('buckets' in j) { setFunnelCompare(j); setFunnel(null); }
        else { setFunnel(j); setFunnelCompare(null); }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setFunnelLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, timeKey, token, funnelCountry, funnelDevice, compareCode, host]);

  // Refresh dashboard + group-by + funnel on range/dim/path/token change, plus 30s interval.
  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeKey, dim, path, token, funnelCountry, funnelDevice, host]);

  return (
    <div className="dashboard">
      <header className="topbar">
        <div>
          <h1>flow-tracker-lite</h1>
          <span className="muted small">{API_BASE}</span>
        </div>
        <div className="controls">
          <select
            value={range}
            onChange={(e) => {
              const next = e.target.value as RangeMode;
              if (next === 'custom' && (!customFrom || !customTo)) {
                // Seed sensible defaults so the user isn't staring at empty inputs.
                if (!customFrom) setCustomFrom(isoDate(-7));
                if (!customTo) setCustomTo(isoDate(0));
              }
              setRange(next);
            }}
          >
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom…</option>
          </select>
          {range === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                aria-label="From date"
                style={{ padding: '4px 8px' }}
              />
              <span className="muted small">→</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                max={isoDate(0)}
                onChange={(e) => setCustomTo(e.target.value)}
                aria-label="To date"
                style={{ padding: '4px 8px' }}
              />
            </>
          )}
          <select value={host} onChange={(e) => setHost(e.target.value)} aria-label="Filter by host">
            <option value="">All hosts</option>
            {hosts.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <button onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button onClick={toggleTheme} className="ghost" aria-label="Toggle dark mode">
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <button onClick={onLogout} className="ghost">Sign out</button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {data && (
        <>
          <section className="cards">
            <Card label="Events" value={data.totals.events} />
            <Card label="Visitors (vid)" value={data.totals.visitors} />
            <Card label="Clicks (int_clickid)" value={data.totals.clicks} />
          </section>

          <ExperimentsPanel
            data={experiments}
            paths={paths}
            range={range}
            onPick={async (p, code) => {
              setPath(p);
              setCompareCode(code);
              // Force a direct fetch with the explicit code — when path and
              // compareCode are already at target values (e.g. from
              // localStorage) the setters bail out of re-rendering and the
              // funnel useEffect never re-runs.
              setFunnelLoading(true);
              try {
                const j = await loadFunnel(p, code);
                if (j && 'buckets' in j) {
                  setFunnelCompare(j);
                  setFunnel(null);
                } else if (j) {
                  setFunnel(j);
                  setFunnelCompare(null);
                }
                // Scroll to the funnel so the user sees the result.
                setTimeout(() => {
                  document.querySelector('.funnel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setFunnelLoading(false);
              }
            }}
          />

          <FunnelPanel
            paths={paths}
            path={path}
            setPath={setPath}
            data={funnel}
            compareData={funnelCompare}
            loading={funnelLoading}
            availableCountries={distinctValues(data.recent_clicks, (r) => r.country)}
            country={funnelCountry}
            setCountry={setFunnelCountry}
            device={funnelDevice}
            setDevice={setFunnelDevice}
            compareCode={compareCode}
            setCompareCode={setCompareCode}
          />

          <AbandonPointsPanel data={abandons} />

          <WebVitalsPanel data={webVitals} />

          <FormHealthPanel data={formHealth} />

          <GroupByPanel
            dimensions={dimensions}
            dim={dim}
            setDim={setDim}
            data={groupBy}
          />

          <section className="recent-clicks-section">
            <div className="recent-clicks-header">
              <div>
                <h2>Recent clicks</h2>
                <p className="muted small subhead">Last 100 clicks · click for full timeline</p>
              </div>
              <button onClick={load} disabled={loading} className="recent-refresh">
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <RecentClicksTable
              rows={data.recent_clicks}
              onSelect={setSelectedClickid}
              selectedClickid={selectedClickid}
              token={token}
              onUnauthorized={onLogout}
            />
          </section>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [token, setToken] = useToken();
  const [theme, toggleTheme] = useTheme();
  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={() => setToken(null)} theme={theme} toggleTheme={toggleTheme} />;
}
